type DirectUploadKind = "ai-music-video" | "voice-cover";
type DirectUploadAssetType = "audio" | "video";

export type UploadProgress = {
  loaded: number;
  total: number;
  percent: number;
  phase: "direct" | "relay";
};

type DirectUploadOptions = {
  kind: DirectUploadKind;
  assetType: DirectUploadAssetType;
  onProgress?: (progress: UploadProgress) => void;
};

type DirectUploadResult = {
  url: string;
  key: string;
};

/**
 * The server rejected the upload because the session cookie is missing or
 * invalid (it can outlive the UI's cached "logged in" state — cleared cookies,
 * rotated secrets). Pages should treat this as "please sign in again", not as
 * an upload problem: retrying without re-authenticating can never succeed.
 */
export class UploadAuthError extends Error {
  constructor() {
    super("Your session has expired. Please sign in again, then retry.");
    this.name = "UploadAuthError";
  }
}

const isAuthStatus = (status: number) => status === 401 || status === 403;

const parseJsonText = (text: string) => {
  if (!text) return null;
  try {
    return JSON.parse(text) as any;
  } catch {
    return null;
  }
};

const parseJsonResponse = async (response: Response) => {
  const text = await response.text();
  return parseJsonText(text);
};

const getContentType = (file: File) =>
  file.type ||
  (file.name.toLowerCase().endsWith(".mp4") ? "video/mp4" : "audio/mpeg");

function uploadWithProgress(input: {
  url: string;
  method: "POST" | "PUT";
  headers?: Record<string, string>;
  body: File;
  withCredentials?: boolean;
  phase: UploadProgress["phase"];
  onProgress?: (progress: UploadProgress) => void;
}): Promise<{ ok: boolean; status: number; responseText: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(input.method, input.url);
    xhr.withCredentials = Boolean(input.withCredentials);

    Object.entries(input.headers ?? {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || event.total <= 0) return;
      input.onProgress?.({
        loaded: event.loaded,
        total: event.total,
        percent: Math.min(99, Math.round((event.loaded / event.total) * 100)),
        phase: input.phase,
      });
    };

    xhr.onload = () => {
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        responseText: xhr.responseText,
      });
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.onabort = () => reject(new Error("Upload cancelled"));
    xhr.send(input.body);
  });
}

async function relayUpload(file: File, options: DirectUploadOptions) {
  const params = new URLSearchParams({
    kind: options.kind,
    assetType: options.assetType,
    fileName: file.name || "upload",
  });

  const attempt = () =>
    uploadWithProgress({
      url: `/api/upload/direct?${params.toString()}`,
      method: "POST",
      withCredentials: true,
      headers: {
        "Content-Type": getContentType(file),
      },
      body: file,
      phase: "relay",
      onProgress: options.onProgress,
    });

  let response;
  try {
    response = await attempt();
  } catch (error) {
    if (error instanceof Error && error.message === "Upload cancelled") {
      throw error;
    }
    // xhr.onerror = the connection itself died (flaky network, proxy reset).
    // One retry rescues most transient drops; a second failure gets a message
    // that names the network instead of a bare "Upload failed".
    await new Promise((resolve) => setTimeout(resolve, 1500));
    try {
      response = await attempt();
    } catch (retryError) {
      if (
        retryError instanceof Error &&
        retryError.message === "Upload cancelled"
      ) {
        throw retryError;
      }
      throw new Error(
        "Network error during upload — check your connection and try again.",
      );
    }
  }
  const payload = parseJsonText(response.responseText);

  if (!response.ok) {
    if (isAuthStatus(response.status)) {
      throw new UploadAuthError();
    }
    throw new Error(payload?.error || `Upload failed (${response.status})`);
  }

  if (!payload?.url || !payload?.key) {
    throw new Error("Upload failed");
  }

  options.onProgress?.({
    loaded: file.size,
    total: file.size,
    percent: 100,
    phase: "relay",
  });

  return payload as DirectUploadResult;
}

export async function uploadFileDirect(
  file: File,
  options: DirectUploadOptions,
): Promise<DirectUploadResult> {
  const contentType = getContentType(file);
  try {
    const presignResponse = await fetch("/api/upload/presign", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        kind: options.kind,
        assetType: options.assetType,
        fileName: file.name || "upload",
        contentType,
      }),
    });
    const presign = await parseJsonResponse(presignResponse);

    if (!presignResponse.ok) {
      // An auth rejection here means the relay would 401 identically —
      // falling through just doubles the server noise and hides the real
      // problem from the user. Surface it as a sign-in problem immediately.
      if (isAuthStatus(presignResponse.status)) {
        throw new UploadAuthError();
      }
      throw new Error(
        presign?.error || `Upload failed (${presignResponse.status})`,
      );
    }

    if (!presign?.uploadUrl || !presign?.url || !presign?.key) {
      throw new Error("Upload failed");
    }

    const uploadResponse = await uploadWithProgress({
      url: presign.uploadUrl,
      method: "PUT",
      headers: {
        "Content-Type": contentType,
      },
      body: file,
      phase: "direct",
      onProgress: options.onProgress,
    });

    if (uploadResponse.ok) {
      options.onProgress?.({
        loaded: file.size,
        total: file.size,
        percent: 100,
        phase: "direct",
      });
      return {
        url: presign.url,
        key: presign.key,
      };
    }
  } catch (error) {
    if (error instanceof UploadAuthError) {
      throw error;
    }
    console.warn("[Upload] Direct S3 upload failed; using API relay", error);
  }

  return relayUpload(file, options);
}
